/**
 * Folder/conversation consistency helpers.
 *
 * Folder membership is stored on `conversations.folder_id` with NO foreign key
 * to `folders.id`, so a conversation can end up tagged with a folder that no
 * longer exists (its creation failed after the move, or it was deleted on
 * another device). Such a conversation renders in "Uncategorized" but keeps a
 * dead tag in the database. This helper finds those so the UI can clear them.
 */

import type { Conversation } from '@/types/chat.types';
import type { Folder } from '@/types/storage.types';

/**
 * Ids of conversations whose `folderId` is set but points at a folder absent
 * from `folders`. A null/undefined/empty folderId means "not filed" and is
 * never an orphan.
 *
 * NOTE: this is a pure set difference — it has no idea whether `folders` is the
 * real, fully-loaded list. Callers MUST only act on the result once the folder
 * list reflects real server data (not an empty loading placeholder), or every
 * filed conversation will look orphaned.
 */
export function findOrphanedConversationIds(
  conversations: Conversation[],
  folders: Folder[]
): string[] {
  const folderIds = new Set(folders.map((f) => f.id));
  return conversations
    .filter(
      (c) => c.folderId != null && c.folderId !== '' && !folderIds.has(c.folderId)
    )
    .map((c) => c.id);
}

export interface AddToFolderCheck {
  ok: boolean;
  /** User-facing reason when `ok` is false. */
  message?: string;
}

/**
 * Decide whether a conversation can be filed into a folder. Blocks two states
 * that would otherwise corrupt or silently no-op:
 *  - the target folder no longer exists (would orphan the conversation), and
 *  - the conversation isn't persisted yet (the backend PUT updates 0 rows).
 */
export function evaluateAddToFolder(
  conversationId: string,
  folderId: string,
  conversations: Conversation[],
  folders: Folder[]
): AddToFolderCheck {
  if (!folders.some((f) => f.id === folderId)) {
    return {
      ok: false,
      message: 'That folder no longer exists. Refresh and try again.',
    };
  }
  if (!conversations.some((c) => c.id === conversationId)) {
    return {
      ok: false,
      message:
        'This conversation hasn’t finished saving yet. Try again in a moment.',
    };
  }
  return { ok: true };
}
